/**
 * PdfContentText component
 * @param {ITextItem[]} items - The list of text items to render
 * @param {boolean} [withoutBottomMargin=false] - Whether to remove the bottom margin from the last item
 * @param {boolean} [withoutGapBetweenItems=false] - Whether to remove the gap between items
 * @returns {React.ReactElement} A React component that displays a formatted text content
 *
 */

import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { globalStyles } from 'PDF/styles';

export interface ITextItem {
  subHeadingText?: string;
  text: string;
}

interface IPdfContentTextProps {
  items: ITextItem[];
  withoutBottomMargin?: boolean;
  withoutGapBetweenItems?: boolean;
}

const checkIsEmptyMagazineText = (items: ITextItem[]) => {
  for (const item of items) {
    if (!Array.isArray(item) && item.text !== '') {
      return false;
    }
  }

  return true;
};

const getTextMargin = (
  isLastItem: boolean,
  withoutGapBetweenItems: boolean,
  isOnlyItem: boolean,
  withoutBottomMargin: boolean,
): number => {
  if (isOnlyItem && withoutBottomMargin) {
    return 6;
  }
  if (isOnlyItem && !withoutBottomMargin) {
    return 0;
  }
  if (!isOnlyItem && withoutBottomMargin) {
    return 6;
  }
  if (isLastItem) {
    if (withoutGapBetweenItems || withoutBottomMargin) {
      return 6;
    }
    return 0;
  }
  return withoutGapBetweenItems ? 0 : 6;
};

export const PdfContentText = ({
  items,
  withoutBottomMargin = false,
  withoutGapBetweenItems = false,
}: IPdfContentTextProps) => {
  if (!items) {
    return null;
  }

  let marginBottom;

  return (
    <>
      {items.filter(item => Boolean(item.text)).map((item, index, currentArray) => {
        const isLastItem = index === currentArray.length - 1;
        const isOnlyItem = currentArray.length === 1;
        marginBottom = getTextMargin(isLastItem, withoutGapBetweenItems, isOnlyItem, withoutBottomMargin);

        return (
          <View key={index} style={{ marginBottom }}>
            <Text style={styles.textMainStyle}>
              <Text style={styles.subHeading}>{item.subHeadingText}</Text>
              <Text>{item.text.replace(/\r?\n|\r/g, '')}</Text>
            </Text>
          </View>
        );
      })}
      {marginBottom === 0 && <View style={checkIsEmptyMagazineText(items) ? {} : globalStyles.marginBottom} />}
    </>
  );
};

const styles = StyleSheet.create({
  textMainStyle: {
    fontFamily: 'Noto',
    fontSize: '10px',
    fontWeight: 300,
    color: '#212121',
    lineHeight: 1.4,
  },
  subHeading: {
    fontWeight: 700,
  },
});
